"use client";

import type { ChladniParamsDefault, ChladniParamsSources } from "@ui/lib/chladni";
import type { MotionValue } from "motion/react";
import { animate, useMotionValue } from "motion/react";
import { createContext } from "react";
import { mergeProps, useHover } from "react-aria";

type MotionRecord<T> = {
  [Key in keyof T]: T[Key] extends [number, number] ? [MotionValue<number>, MotionValue<number>] : MotionValue<T[Key]>;
};

type ContextValue =
  | (ChladniParamsSources & {
      motionValues: MotionRecord<Omit<ChladniParamsSources, "mode" | "symmetry">>;
    })
  | (ChladniParamsDefault & {
      motionValues: MotionRecord<Required<Omit<ChladniParamsDefault, "mode" | "symmetry" | "anchor">>>;
    });

export const ChladniHoverContext = createContext<ContextValue | null>(null);

export function ChladniHoverContainer({
  children,
  chladni,
  ...props
}: React.ComponentPropsWithRef<"div"> & {
  chladni:
    | (ChladniParamsSources & { hover: Partial<ChladniParamsSources> })
    | (ChladniParamsDefault & { hover: Partial<ChladniParamsDefault> });
}) {
  const motionValues = [
    useMotionValue(chladni.mode === "sources" ? chladni.frequency : chladni.a),
    useMotionValue(chladni.mode === "sources" ? chladni.phaseShift : chladni.b),
    useMotionValue(chladni.mode === "sources" ? chladni.elasticity : chladni.m),
    useMotionValue(chladni.mode === "sources" ? chladni.sources : chladni.n),
    useMotionValue(chladni.quality),
    useMotionValue(chladni.mode === "default" ? (chladni.origin?.[0] ?? 0) : 0),
    useMotionValue(chladni.mode === "default" ? (chladni.origin?.[1] ?? 0) : 0),
    useMotionValue(chladni.mode === "default" ? (chladni.length ?? 0) : 0),
  ];

  const { hoverProps } = useHover({
    onHoverStart() {
      animate(
        [
          [
            motionValues[0],
            chladni.mode === "sources"
              ? (chladni.hover.frequency ?? chladni.frequency)
              : (chladni.hover.a ?? chladni.a),
            { at: 0 },
          ],
          [
            motionValues[1],
            chladni.mode === "sources"
              ? (chladni.hover.phaseShift ?? chladni.phaseShift)
              : (chladni.hover.b ?? chladni.b),
            { at: 0 },
          ],
          [
            motionValues[2],
            chladni.mode === "sources"
              ? (chladni.hover.elasticity ?? chladni.elasticity)
              : (chladni.hover.m ?? chladni.m),
            { at: 0 },
          ],
          [
            motionValues[3],
            chladni.mode === "sources" ? (chladni.hover.sources ?? chladni.sources) : (chladni.hover.n ?? chladni.n),
            { at: 0 },
          ],
          [motionValues[4], chladni.hover.quality ?? chladni.quality, { at: 0 }],
        ],
        {
          defaultTransition: { duration: 0.6, ease: [0.2, 0, 0.3, 1] },
        },
      );
    },
    onHoverEnd() {
      animate(
        [
          [motionValues[0], chladni.mode === "sources" ? chladni.frequency : chladni.a, { at: 0 }],
          [motionValues[1], chladni.mode === "sources" ? chladni.phaseShift : chladni.b, { at: 0 }],
          [motionValues[2], chladni.mode === "sources" ? chladni.elasticity : chladni.m, { at: 0 }],
          [motionValues[3], chladni.mode === "sources" ? chladni.sources : chladni.n, { at: 0 }],
          [motionValues[4], chladni.quality, { at: 0 }],
        ],
        {
          defaultTransition: { duration: 0.6, ease: [0.2, 0, 0.3, 1] },
        },
      );
    },
  });

  return (
    <div {...mergeProps(props, hoverProps)}>
      <ChladniHoverContext.Provider
        value={
          chladni.mode === "sources"
            ? {
                ...chladni,
                motionValues: {
                  frequency: motionValues[0],
                  phaseShift: motionValues[1],
                  elasticity: motionValues[2],
                  sources: motionValues[3],
                  quality: motionValues[4],
                },
              }
            : {
                ...chladni,
                motionValues: {
                  a: motionValues[0],
                  b: motionValues[1],
                  m: motionValues[2],
                  n: motionValues[3],
                  quality: motionValues[4],
                  origin: [motionValues[5], motionValues[6]],
                  length: motionValues[7],
                },
              }
        }
      >
        {children}
      </ChladniHoverContext.Provider>
    </div>
  );
}
